/**
 * МНЕНИЕ АВТОРА КАНАЛА НА ЭКРАНЕ ЧЕРНОВИКА — что показать и что можно сделать (AGI Boardroom,
 * build spec 2026-11-02 §4, контракт — `api/author-opinion.ts`). Вывод один и здесь, по образцу
 * `visual/visualState.ts`: полоса и её диалог читают готовое значение, а не разбирают модель
 * рендера каждый по-своему.
 *
 * ЖИВОЕ И ЗАКРЫТОЕ. Модель рендера несёт мнение черновика с его состоянием (`AuthorOpinionView`).
 * Живых состояний два — `pending` (вставка заказана, очищенного текста ещё нет) и `active`
 * (очищенный текст в посте, переносится в каждую переделку); с ними полоса показывает мнение и
 * даёт «Изменить» и «Убрать». Остальные — закрытые: у черновика живого мнения нет, и полоса даёт
 * «Добавить». О закрытом она говорит строкой, только когда человек мог этого не заметить: мнение
 * ушло из текста правкой (`detached`) или вставка не состоялась (`cancelled`). Снятое по просьбе
 * (`removed`) и заменённое (`superseded`) — его собственные решения, и пересказывать их незачем.
 */

import { DRAFT_TRANSITIONS, type DraftState } from '@smm/contracts';
import { ApiRequestError } from '../../api/client.ts';
import type { AuthorOpinionView, RenderModel } from '../../api/types.ts';
import { lockReason, OPINION_OBJECT } from '../versionLock.ts';

/**
 * Состояния версии, из которых мнение можно отправить: те, откуда есть ребро в «на переделке».
 * Мнение едет переделкой (`opinion:insert` / `opinion:remove`), и бэкенд проверяет ровно это
 * ребро; набор выведен из таблицы переходов, а не переписан списком — второй список разошёлся бы
 * с ней молча (`pending_review`, `approved`, `deferred`).
 */
export const OPINION_REVISABLE: ReadonlySet<DraftState> = new Set(
  DRAFT_TRANSITIONS.filter((t) => t.to === 'changes_requested').map((t) => t.from),
);

/** Что показывает полоса мнения. */
export type OpinionStripView =
  /** Живого мнения нет; `last` — закрытое мнение, о судьбе которого стоит сказать, или `null`. */
  | { readonly state: 'none'; readonly last: AuthorOpinionView | null }
  /** Вставка заказана: очищенный текст и место в посте придут с новой версией. */
  | { readonly state: 'pending'; readonly opinion: AuthorOpinionView }
  /** Мнение действует: очищенный текст есть и переносится в каждую переделку дословно. */
  | { readonly state: 'active'; readonly opinion: AuthorOpinionView };

export function opinionViewOf(model: Pick<RenderModel, 'author_opinion'>): OpinionStripView {
  // Нет ключа — ответ бэкенда старше поля, читается как «мнения нет» (`RenderModelSchema`).
  const op = model.author_opinion ?? null;
  if (op === null) return { state: 'none', last: null };
  if (op.state === 'pending') return { state: 'pending', opinion: op };
  if (op.state === 'active') return { state: 'active', opinion: op };
  return {
    state: 'none',
    last: op.state === 'detached' || op.state === 'cancelled' ? op : null,
  };
}

/** Строка рядом с заголовком полосы: где мнение относительно ЭТОЙ версии. */
export function opinionHeadline(view: OpinionStripView): string {
  switch (view.state) {
    case 'none':
      return 'мнения нет';
    case 'pending':
      return 'вставляется';
    case 'active':
      return view.opinion.in_version === true ? 'есть в тексте' : 'нет в тексте';
  }
}

/**
 * Что значит строка рядом с заголовком — подсказкой у неё: объяснение величины стоит рядом с
 * величиной, а не мелким текстом в теле полосы (docs/frontend.md §«ⓘ вместо мелкого текста»).
 */
export function opinionHeadlineTitle(view: OpinionStripView): string | null {
  switch (view.state) {
    case 'none':
      return null;
    case 'pending':
      return 'Мнение ушло сервису генерации: очищенный текст и его место в посте придут с новой версией.';
    case 'active':
      return view.opinion.in_version === true
        ? 'Очищенное мнение стоит в тексте этой версии дословно.'
        : 'Очищенного мнения нет в тексте этой версии дословно — обычно это версия, сделанная до вставки.';
  }
}

/** Что стало с прошлым мнением — строкой полосы; `null` — говорить не о чем. */
export function opinionLastNote(view: OpinionStripView): string | null {
  if (view.state !== 'none' || view.last === null) return null;
  return view.last.state === 'detached'
    ? 'прошлое мнение убрано из текста правкой — в переделки оно больше не переносится'
    : 'прошлое мнение в пост не попало: переделка закрылась без него';
}

/**
 * Почему мнение у этой версии не отправить — словами, с маленькой буквы, без точки: встаёт после
 * имени действия («Добавить мнение — …», `offLabel`). `null` — отправить можно. «На переделке» —
 * своя фраза: правка в этом состоянии разрешена (полосы картинки и файла её принимают), а вторая
 * переделка поверх идущей — нет.
 */
export function opinionLockReason(status: DraftState): string | null {
  if (OPINION_REVISABLE.has(status)) return null;
  if (status === 'changes_requested') return 'пост уже на переделке — дождитесь новой версии';
  return `версия не меняется: ${lockReason(status, OPINION_OBJECT)}`;
}

/**
 * Код отказа «у бренда мнение автора выключено» (409, build spec 2026-11-02 §4). Распознаётся и
 * кодом ошибки, и `details.reason` — так же, как бэкенд отдаёт отказ бренда без сервиса
 * (`BRAND_AGENTS_OFF_REASON`): диалог обязан показать текст сервера, а не прочитать отказ как
 * «черновик изменился».
 */
export const AUTHOR_OPINION_OFF = 'author_opinion_off';

export function isAuthorOpinionOff(e: unknown): e is ApiRequestError {
  if (!(e instanceof ApiRequestError)) return false;
  if (e.code === AUTHOR_OPINION_OFF) return true;
  const d = e.details;
  return (
    typeof d === 'object' &&
    d !== null &&
    (d as Record<string, unknown>)['reason'] === AUTHOR_OPINION_OFF
  );
}
